import { Collapse, List, ListItem, Typography } from "@mui/material";
import React, { useState } from "react";

import FilterRow, { FILTER_ROW_INDENT } from "./FilterRow";

// One row of a filter tree
export interface FilterTreeNode {
  // What picking the row picks
  value: string;
  label: string;
  // The count to show; the tree computes none
  count: number;
  // The rows beneath it, as far as they are known
  children?: FilterTreeNode[];
  // Set for a row that has rows beneath it which were not given: the row can
  // be opened, and opening it asks for them (`onRequestChildren`)
  hasChildren?: boolean;
}

export interface FilterTreeProps {
  // What the tree filters by; names the list
  label: string;
  // The roots. The tree renders the levels it is given.
  nodes: FilterTreeNode[];
  // The values picked, wherever they are in the tree
  selected: readonly string[];
  onToggle: (value: string, selected: boolean) => void;
  // Called when a row is opened whose rows beneath it were not given, so
  // that the owner can fetch them and pass the tree with them filled in
  onRequestChildren?: (node: FilterTreeNode) => void;
}

// What parts the values of a path. A value never holds it.
const PATH_SEPARATOR = "\u0000";

const holdsSelected = (
  node: FilterTreeNode,
  selected: readonly string[],
): boolean =>
  (node.children ?? []).some(
    (child) => selected.includes(child.value) || holdsSelected(child, selected),
  );

// A filter as a tree: rows with a checkbox, a label, a count and an
// expander, a level beneath a row indented. A row starts closed, unless a
// row beneath it is picked. Every row is picked by itself; what picking a
// row includes is the owner's matter.
const FilterTree: React.FC<FilterTreeProps> = ({
  label,
  nodes,
  selected,
  onToggle,
  onRequestChildren,
}) => {
  // The rows the user opened or closed, by path; the others are as they start
  const [opened, setOpened] = useState<Record<string, boolean>>({});

  const renderLevel = (
    level: FilterTreeNode[],
    depth: number,
    parentPath: string,
  ): React.ReactNode =>
    level.map((node) => {
      const path = parentPath + PATH_SEPARATOR + node.value;
      const children = node.children ?? [];
      const expandable = children.length > 0 || Boolean(node.hasChildren);
      const open = opened[path] ?? holdsSelected(node, selected);
      const awaited = open && children.length === 0;

      return (
        <React.Fragment key={node.value}>
          <FilterRow
            label={node.label}
            count={node.count}
            checked={selected.includes(node.value)}
            onChange={(checked) => onToggle(node.value, checked)}
            depth={depth}
            expander={expandable ? (open ? "expanded" : "collapsed") : "gap"}
            onExpanderClick={() => {
              setOpened((current) => ({ ...current, [path]: !open }));
              if (!open && children.length === 0) {
                onRequestChildren?.(node);
              }
            }}
          />
          {expandable && (
            <Collapse in={open} component="li" timeout={120} unmountOnExit>
              {awaited ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    pl: `${(depth + 1) * FILTER_ROW_INDENT}px`,
                    py: 0.5,
                  }}
                >
                  Loading…
                </Typography>
              ) : (
                <List disablePadding aria-label={node.label}>
                  {renderLevel(children, depth + 1, path)}
                </List>
              )}
            </Collapse>
          )}
        </React.Fragment>
      );
    });

  if (nodes.length === 0) {
    return (
      <List disablePadding aria-label={label}>
        <ListItem disablePadding>
          <Typography variant="caption" color="text.secondary">
            No values
          </Typography>
        </ListItem>
      </List>
    );
  }

  return (
    <List disablePadding aria-label={label}>
      {renderLevel(nodes, 0, "")}
    </List>
  );
};

export default FilterTree;
